import { API_URL } from '../config.js';
import { audioEvents } from '../audio/audioEvents.js';
import { noteServerTime } from '../state/serverClock.mjs';

// Called once when the server says we're not logged in mid-game (main.js sends her to the login).
let onSessionLost = null;
export function setSessionLostHandler(fn) { onSessionLost = fn; }

// Requests still on the way: a new-version reload waits for them (ui/autoUpdate.js), or a save could be cut off.
let inflight = 0;
export const requestsInFlight = () => inflight;
async function request(path, opts) {
  inflight++;
  try { return await send(path, opts); } finally { inflight--; }
}

async function send(path, { method = 'GET', body } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  // Who this tab plays as: the server refuses a save meant for her landing on whoever the cookie
  // now belongs to (another tab logged into someone else).
  if (typeof window !== 'undefined' && window.__myId) headers['X-Player-Id'] = window.__myId;

  const doFetch = () => fetch(`${API_URL}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

  let res;
  const sentMs = Date.now();
  try {
    res = await doFetch();
  } catch (_) {
    // Retry once after 2s on network failure
    await new Promise(r => setTimeout(r, 2000));
    try {
      res = await doFetch();
    } catch (_) {
      throw { error_code: 'NETWORK' };
    }
  }

  if (res.ok) {
    const json = await res.json();
    if (json && typeof json === 'object' && 'server_now_ms' in json) noteServerTime(json.server_now_ms, sentMs, Date.now());
    return json;
  }

  let detail = null;
  try {
    const body = await res.json();
    detail = body.detail;
  } catch (_) {
    detail = { error_code: res.status >= 500 ? 'SERVER' : 'UNKNOWN' };
  }
  if (res.status !== 401) audioEvents.emit('error');
  // Logged out under her (the login ran out, or another tab logged out): before 2026-10-18 the game
  // played on and every save failed silently. The login screen's own checks are not this.
  else if (!path.startsWith('/api/auth/')) onSessionLost?.();
  throw { status: res.status, detail };
}

export const api = {
  register: (name, password, character = 'natalka', look = null) => request('/api/auth/register', { method: 'POST', body: { name, password, character, look } }),
  putLook: (look) => request('/api/auth/look', { method: 'PUT', body: { look } }),
  login:    (name, password) => request('/api/auth/login',    { method: 'POST', body: { name, password } }),
  logout:   () => request('/api/auth/logout', { method: 'POST', body: {} }),
  quickLogin:  (token) => request('/api/auth/quick-login',  { method: 'POST', body: { token } }),
  quickForget: (token) => request('/api/auth/quick-forget', { method: 'POST', body: { token } }),
  me:       () => request('/api/auth/me'),
  getState: () => request('/api/state'),
  farmActions: (actions) => request('/api/farm/actions', { method: 'POST', body: { actions } }),
  unlockHouse: () => request('/api/actions/unlock-house', { method: 'POST', body: {} }),
  buyFurniture: (item) => request('/api/actions/buy-furniture', { method: 'POST', body: { item } }),
  buyDecoration: (item) => request('/api/actions/buy-decoration', { method: 'POST', body: { item } }),
  unlockRoom: (room) => request('/api/actions/unlock-room', { method: 'POST', body: { room } }),
  resetGame: () => request('/api/actions/reset-game', { method: 'POST', body: {} }),
  // Sub-project A
  buyBuda:        (item)            => request('/api/actions/buy-buda',       { method: 'POST', body: { item } }),
  buyFood:        (item)            => request('/api/actions/buy-food',       { method: 'POST', body: { item } }),
  buyAnimal:      (type, budaId = null) => request('/api/actions/buy-animal',     { method: 'POST', body: { type, buda_placement_id: budaId } }),
  buyClothing:    (item)            => request('/api/actions/buy-clothing',   { method: 'POST', body: { item } }),
  buyBoardGame:   (gameId)          => request('/api/actions/buy-board-game', { method: 'POST', body: { game_id: gameId } }),
  placeObject:    (type, item, scene, x, y) => request('/api/actions/place-object',
    { method: 'POST', body: { type, item, scene, x: Math.round(x), y: Math.round(y) } }),
  moveObject:     (placementId, x, y) => request('/api/actions/move-object',
    { method: 'POST', body: { placement_id: placementId, x: Math.round(x), y: Math.round(y) } }),
  removeObject:   (placementId)     => request('/api/actions/remove-object',
    { method: 'POST', body: { placement_id: placementId } }),
  sellObject:     (placementId)     => request('/api/actions/sell-object',
    { method: 'POST', body: { placement_id: placementId } }),
  toggleFlip:     (placementId)     => request('/api/actions/toggle-flip',
    { method: 'POST', body: { placement_id: placementId } }),
  // A bike is named by its design id (bike plan 6: one of each design).
  buyBike:        (design)            => request('/api/actions/buy-bike',    { method: 'POST', body: { design } }),
  sellBike:       (bike)              => request('/api/actions/sell-bike',   { method: 'POST', body: { bike } }),
  buyBikeGrease:  ()                  => request('/api/actions/buy-bike-grease', { method: 'POST', body: {} }),
  greaseBike:     (bike)              => request('/api/actions/grease-bike', { method: 'POST', body: { bike } }),
  consumeBikeGrease: (amount, bike)   => request('/api/actions/consume-bike-grease', { method: 'POST', body: { amount, bike } }),
  parkBike:       (scene, x, bike, facing = 0, y = null) => request('/api/actions/park-bike', { method: 'POST', body: { scene, x, y, bike, facing } }),
  setLocation:    (scene, x, y, facing = null) => request('/api/actions/set-location', { method: 'POST', body: { scene, x, y, facing } }),
  feedAnimal:     (animalId)        => request('/api/actions/feed-animal',
    { method: 'POST', body: { animal_id: animalId } }),
  petAnimal:      (animalId)        => request('/api/actions/pet-animal',
    { method: 'POST', body: { animal_id: animalId } }),
  collectProduct: (animalId)        => request('/api/actions/collect-product',
    { method: 'POST', body: { animal_id: animalId } }),
  equipClothing:  (item)            => request('/api/actions/equip-clothing', { method: 'POST', body: { item } }),
  unequipClothing:(category)        => request('/api/actions/unequip-clothing', { method: 'POST', body: { category } }),
  sellProduct:    (item, qty=1)     => request('/api/actions/sell-product',
    { method: 'POST', body: { item, quantity: qty } }),
  setClothTweak:  (item, tweak)     => request('/api/actions/set-cloth-tweak',
    { method: 'POST', body: { item, ...tweak } }),
  resetClothTweak:(item)            => request('/api/actions/reset-cloth-tweak',
    { method: 'POST', body: { item } }),
  // Sub-project B
  upgradeHouse:  ()             => request('/api/actions/upgrade-house', { method: 'POST', body: {} }),
  unlockBackyard:  ()            => request('/api/actions/unlock-backyard',  { method: 'POST', body: {} }),
  upgradeBackyard: ()            => request('/api/actions/upgrade-backyard', { method: 'POST', body: {} }),
  devAddMoney:   ()             => request('/api/actions/dev-add-money',  { method: 'POST', body: {} }),
  // Game Hall (Plan 3)
  gameHallRankings: () => request('/api/game-hall/rankings'),
  stats: () => request('/api/stats'),
  // Driving school
  drivingBuyCourse:      ()            => request('/api/actions/driving/buy-course', { method: 'POST', body: {} }),
  drivingQuizStart:      (kind)        => request('/api/actions/driving/quiz-start', { method: 'POST', body: { kind } }),
  drivingQuizAnswer:     (questionId, answer) => request('/api/actions/driving/quiz-answer',
    { method: 'POST', body: { question_id: questionId, answer } }),
  drivingQuizAbort:      ()            => request('/api/actions/driving/quiz-abort', { method: 'POST', body: {} }),
  drivingDevGrantLicence:()            => request('/api/actions/driving/dev-grant-licence', { method: 'POST', body: {} }),
  // Study material (answers included) — fetched on demand, NOT part of the catalog.
  drivingLessons:        ()            => request('/api/driving/lessons'),
  // Practical lessons + exam (spec 3) — responses carry an extra `practical` key.
  drivingLessonStart:     (n)                => request('/api/actions/driving/lesson-start', { method: 'POST', body: { n } }),
  drivingLessonFinish:    (tasks)            => request('/api/actions/driving/lesson-finish', { method: 'POST', body: { tasks } }),
  drivingLessonAbort:     ()                 => request('/api/actions/driving/lesson-abort', { method: 'POST', body: {} }),
  drivingPracticalStart:  ()                 => request('/api/actions/driving/practical-start', { method: 'POST', body: {} }),
  drivingPracticalFinish: (tasks)            => request('/api/actions/driving/practical-finish', { method: 'POST', body: { tasks } }),
  drivingPracticalAbort:  ()                 => request('/api/actions/driving/practical-abort', { method: 'POST', body: {} }),

  // Cars — every one of these returns the full state (`state.cars`), like the actions above.
  // `leave-garage` additionally carries `car`, `refuel` carries `refuel`.
  carsBuy:            (model)                 => request('/api/actions/cars/buy',           { method: 'POST', body: { model } }),
  carsSell:           (carId)                 => request('/api/actions/cars/sell',          { method: 'POST', body: { car_id: carId } }),
  carsRepair:         (carId)                 => request('/api/actions/cars/repair',        { method: 'POST', body: { car_id: carId } }),
  carsLeaveGarage:    (carId)                 => request('/api/actions/cars/leave-garage',  { method: 'POST', body: { car_id: carId } }),
  carsPark:           (carId, x, facing)      => request('/api/actions/cars/park',          { method: 'POST', body: { car_id: carId, x: Math.round(x), facing } }),
  carsParkGarage:     (carId)                 => request('/api/actions/cars/park-garage',   { method: 'POST', body: { car_id: carId } }),
  carsSetChargeLimit: (carId, limit)          => request('/api/actions/cars/set-charge-limit', { method: 'POST', body: { car_id: carId, limit } }),
  carsDriveReport:    (carId, meters, fuel)   => request('/api/actions/cars/drive-report',  { method: 'POST', body: { car_id: carId, meters, fuel } }),
  carsRefuel:         (carId, nozzle, liters) => request('/api/actions/cars/refuel',        { method: 'POST', body: { car_id: carId, nozzle, liters } }),
  carsBreakDown:      (carId)                 => request('/api/actions/cars/break-down',    { method: 'POST', body: { car_id: carId } }),
  carsRescue:         (carId)                 => request('/api/actions/cars/rescue',        { method: 'POST', body: { car_id: carId } }),
  carsDevAddCar:      (model)                 => request('/api/actions/cars/dev-add-car',   { method: 'POST', body: { model } }),
};
